/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:06]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    DocumentTitle,
    Form,
    Pending,
    useActionsBtnContext,
    useErrorState,
    usePendingState,
    ValidatedInput,
    WorkplaceSetting,
} from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getWorkplaceSettingsAll, updateTechnicalWorksSettings } from 'axios/workplaceSettingsApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Card, CardBody } from 'reactstrap';

import './WorkplaceSettings.scss';

type Settings = { settings: WorkplaceSetting[] };

const WorkplaceSettings = () => {
    const api = useApi();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { setPending } = usePendingState();
    const { handleRequestError } = useErrorState();
    const { addRequest } = useActionsBtnContext();
    const formRef = useRef<HTMLFormElement>(null);

    const [defaultValues, setDefaultValues] = useState<Settings>({
        settings: [],
    });

    const { data: settings, isFetching } = useQuery(
        'workplaceSettingsQuery',
        () => getWorkplaceSettingsAll(api),
        {
            onSuccess: (data) => {
                setDefaultValues({ settings: data });
            },
            onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
        }
    );

    const { mutate, isLoading } = useMutation(
        'updateTechnicalWorksSettingsMutation',
        (data: WorkplaceSetting[]) => updateTechnicalWorksSettings(api, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['workplaceSettingsQuery']);
            },
            onError: (e: AxiosError<AxiosErrorResponse>) => handleRequestError(e.response?.data),
        }
    );

    const saveChange = (data: Settings) => {
        const updated = data.settings.filter(
            (x) => x.onTechnicalWork !== settings?.find((y) => y.path === x.path)?.onTechnicalWork
        );
        if (updated && updated.length) mutate(updated);
    };

    useEffect(() => setPending(isLoading), [isLoading]);

    const submit = () => {
        addRequest({
            content: t('BackOfficeAreYouSure'),
            confirmCallback: (res) => {
                res && formRef.current && formRef.current.requestSubmit();
            },
        });
    };

    return (
        <DocumentTitle code="BackOfficeWorkplaceSettings">
            <Form<Settings>
                defaultValues={defaultValues}
                onSubmit={saveChange}
                innerRef={formRef}
                className="h-100"
            >
                <Card className="workplace-settings">
                    <CardBody className="workplace-settings__body workplace-settings__content">
                        <h3>{t('BackOfficeShutdownWorkplaces')}</h3>
                        <div className="custom-scrollbar workplace-settings__workplace-list">
                            {isFetching ? (
                                <Pending />
                            ) : (
                                defaultValues.settings.map((x, idx) => (
                                    <ValidatedInput<Settings>
                                        key={x.path}
                                        name={`settings.${idx}.onTechnicalWork`}
                                        type="switch"
                                        role="switch"
                                        label={t(`BackOffice${x.translateCode}`)}
                                        isFloating={false}
                                    />
                                ))
                            )}
                        </div>
                        <WorkplaceSettingsFooter isFetching={isFetching} submit={submit} />
                    </CardBody>
                </Card>
            </Form>
        </DocumentTitle>
    );
};

const WorkplaceSettingsFooter = ({
    isFetching,
    submit,
}: {
    isFetching: boolean;
    submit: () => void;
}) => {
    const { hasRole } = useAuth();
    const { formState } = useFormContext();

    return (
        <div className="workplace-settings__footer">
            <Button
                color="success"
                disabled={
                    isFetching ||
                    !hasRole('workplaceSettingsUpdateOnTechnicalWorks') ||
                    !formState.isDirty
                }
                onClick={submit}
            >
                <Trans i18nKey="BackOfficeSave" />
            </Button>
        </div>
    );
};

export default WorkplaceSettings;
